import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { TableColumn } from '@/components/common/DataTable/types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import {
  userPermissionsService,
  type UserPermission,
  type CreateUserPermissionPayload,
} from '@/superadmin/services/userPermissionsService';
import { UserPermissionMappingFormDrawer } from '../../components';
import { toast } from 'react-hot-toast';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

// const UserPermissionsPage: React.FC = () => {
//   const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [formLoading, setFormLoading] = useState(false);

//   const fetchUserPermissions = async () => {
//     try {
//       setLoading(true);
//       const response = await userPermissionsService.getUserPermissions();
//       console.log('User Permissions API Response:', response);

//       if (response.success && response.data) {
//         setUserPermissions(response.data.results.user_permissions || []);
//       } else {
//         toast.error('Failed to load user permissions');
//       }
//     } catch (error) {
//       console.warn('Failed to load user permissions:', error);
//       toast.error('Failed to load user permissions');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchUserPermissions();
//   }, []);

//   // Breadcrumbs
//   const breadcrumbConfig = getBreadcrumbConfig('/user-permissions');
//   const breadcrumbs = useCommonBreadcrumbs({
//     hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
//   });

//   const finalBreadcrumbs =
//     breadcrumbs.length > 0
//       ? breadcrumbs
//       : [
//           { label: 'Dashboard', href: '/superadmin/dashboard' },
//           { label: 'User Permission', active: true },
//         ];

//   // User permissions table columns
//   const columns: TableColumn<UserPermission>[] = [
//     {
//       field: 'role_permission.permission_name',
//       headerName: 'Permission',
//       minWidth: 200,
//       sortable: true,
//       renderCell: params => (
//         <Typography variant="body2" fontWeight="medium">
//           {params.row.role_permission?.permission_name || String(params.value)}
//         </Typography>
//       ),
//     },
//     {
//       field: 'role_permission',
//       headerName: 'Role Permission',
//       minWidth: 300,
//       sortable: true,
//       renderCell: params => (
//         <Box>
//           <Typography variant="body2" fontWeight="medium">
//             {(params.value as any)?.role_name} - {(params.value as any)?.permission_name}
//           </Typography>
//           <Typography variant="caption" color="textSecondary">
//             {(params.value as any)?.permission_description}
//           </Typography>
//         </Box>
//       ),
//     },
//     {
//       field: 'is_active',
//       headerName: 'Status',
//       minWidth: 120,
//       sortable: true,
//       renderCell: params => (
//         <Chip
//           label={params.value ? 'Active' : 'Inactive'}
//           color={params.value ? 'success' : 'default'}
//           size="small"
//           variant="outlined"
//         />
//       ),
//     },
//     {
//       field: 'granted_at',
//       headerName: 'Granted At',
//       minWidth: 150,
//       sortable: true,
//       renderCell: params => (
//         <Typography variant="body2">
//           {new Date(String(params.value)).toLocaleDateString()}
//         </Typography>
//       ),
//     },
//     {
//       field: 'expires_at',
//       headerName: 'Expires At',
//       minWidth: 150,
//       sortable: true,
//       renderCell: params => (
//         <Typography variant="body2">
//           {params.value ? new Date(String(params.value)).toLocaleDateString() : 'Never'}
//         </Typography>
//       ),
//     },
//     {
//       field: 'granted_by',
//       headerName: 'Granted By',
//       minWidth: 120,
//       sortable: true,
//       renderCell: params => (
//         <Typography variant="body2">
//           {params.value ? String(params.value) : 'System'}
//         </Typography>
//       ),
//     },
//   ];

//   const handleCreateMapping = async (payload: CreateUserPermissionPayload) => {
//     try {
//       setFormLoading(true);
//       const response = await userPermissionsService.createUserPermission(payload);

//       if (response.success) {
//         toast.success(response.message || 'User permission mapping created successfully');
//         setDrawerOpen(false);
//         // Refresh the data to show the new mapping
//         await fetchUserPermissions();
//       } else {
//         toast.error(response.message || 'Failed to create user permission mapping');
//       }
//     } catch (error) {
//       console.warn('Failed to create user permission mapping:', error);
//       toast.error('Failed to create user permission mapping');
//     } finally {
//       setFormLoading(false);
//     }
//   };

//   // Header actions
//   const headerActions: PageHeaderAction[] = [
//     {
//       label: 'Map User Permission',
//       icon: <AddIcon sx={{ fontSize: 16 }} />,
//       onClick: () => setDrawerOpen(true),
//       variant: 'contained',
//       size: 'medium'
//     },
//   ];

//   return (
//     <Box sx={{ width: '100%', overflow: 'hidden' }}>
//       <PageHeader
//         title="User Permission"
//         subtitle="Manage user permissions in the system"
//         breadcrumbs={finalBreadcrumbs}
//         actions={headerActions}
//         showDivider={false}
//       />

//       <Box sx={{ width: '100%', overflowX: 'auto' }}>
//         <DataTable
//         data={userPermissions}
//         columns={columns}
//         keyField="uuid"
//         title="User Permissions"
//         showTitle={false}
//         loading={loading}
//         pagination={true}
//         rowsPerPageOptions={[5, 10, 25, 50]}
//         defaultRowsPerPage={10}
//         sortable={true}
//         stickyHeader={true}
//         maxHeight={600}
//         striped={true}
//         emptyState={
//           <Box display="flex" justifyContent="center" alignItems="center" py={4}>
//             <Typography variant="body1" color="textSecondary">
//               No user permissions found.
//             </Typography>
//           </Box>
//           }
//         />

//         <UserPermissionMappingFormDrawer
//           open={drawerOpen}
//           onClose={() => setDrawerOpen(false)}
//           onSubmit={handleCreateMapping}
//           loading={formLoading}
//         />
//       </Box>
//     </Box>
//   );
// };

const UserPermissionsPage: React.FC = () => {
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  const fetchUserPermissions = async () => {
    try {
      setLoading(true);

      const response = await userPermissionsService.getUserPermissions();
      console.log('User Permissions API Response:', response);

      // Handle real API response structure
      if (response && Array.isArray(response.results)) {
        const data = {
          user_permissions: response.results,
          pagination: {
            count: response.count,
            next: response.next,
            previous: response.previous,
          },
        };

        setUserPermissions(data.user_permissions);
      } else {
        toast.error('Failed to load user permissions');
      }
    } catch (error) {
      console.warn('Failed to load user permissions:', error);
      toast.error('Failed to load user permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
  }, []);

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/user-permissions');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
          { label: 'Dashboard', href: '/superadmin/dashboard' },
          { label: 'User Permission', active: true },
        ];

  // Table Columns
  const columns: TableColumn<UserPermission>[] = [
    {
      field: 'role_permission.permission_name',
      headerName: 'Permission',
      minWidth: 200,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" fontWeight="medium">
          {params.row.role_permission?.permission_name || ''}
        </Typography>
      ),
    },
    {
      field: 'role_permission',
      headerName: 'Role Permission',
      minWidth: 300,
      sortable: true,
      renderCell: params => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {(params.value as any)?.role_name} - {(params.value as any)?.permission_name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {(params.value as any)?.permission_description}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      minWidth: 120,
      sortable: true,
      renderCell: params => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'granted_at',
      headerName: 'Granted At',
      minWidth: 150,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">
          {new Date(String(params.value)).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'expires_at',
      headerName: 'Expires At',
      minWidth: 150,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">
          {params.value ? new Date(String(params.value)).toLocaleDateString() : 'Never'}
        </Typography>
      ),
    },
    {
      field: 'granted_by',
      headerName: 'Granted By',
      minWidth: 120,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">{params.value ? String(params.value) : 'System'}</Typography>
      ),
    },
  ];

  const handleCreateMapping = async (payload: CreateUserPermissionPayload) => {
    try {
      setFormLoading(true);
      const response = await userPermissionsService.createUserPermission(payload);

      if (response.success) {
        toast.success(response.message || 'User permission created successfully');
        setDrawerOpen(false);
        await fetchUserPermissions();
      } else {
        toast.error(response.message || 'Failed to create permission');
      }
    } catch (error) {
      console.warn('Failed to create permission:', error);
      toast.error('Failed to create permission');
    } finally {
      setFormLoading(false);
    }
  };

  const headerActions: PageHeaderAction[] = [
    hasPermission('ADMIN_USER_PERMISSION_MAPPING') && 
    {
      label: 'Map User Permission',
      icon: <AddIcon sx={{ fontSize: 16 }} />,
      onClick: () => setDrawerOpen(true),
      variant: 'contained',
      size: 'medium',
    },
  ].filter(Boolean) as PageHeaderAction[];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <PageHeader
        title="User Permission"
        subtitle="Manage user permissions"
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      {hasPermission('ADMIN_USER_PERMISSION_LIST_VIEW') ? (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <DataTable
            data={userPermissions}
            columns={columns}
            keyField="uuid"
            title="User Permissions"
            showTitle={false}
            loading={loading}
            pagination={true}
            rowsPerPageOptions={[5, 10, 25, 50]}
            defaultRowsPerPage={10}
            sortable={true}
            stickyHeader={true}
            maxHeight={600}
            striped={true}
            emptyState={
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No user permissions found.
                </Typography>
              </Box>
            }
          />

          <UserPermissionMappingFormDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSubmit={handleCreateMapping}
            loading={formLoading}
          />
        </Box>
      ) : (
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view.
        </Typography>
      )}
    </Box>
  );
};

export default UserPermissionsPage;
